import { ChevronDown, LogOut, User as UserIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import type { RootState } from "../../app/store";
import { isReadOnlyPreviewAllowed } from "../../config/env";
import { useLogoutMutation } from "../../features/auth/auth.api";
import type { MockAccessPreviewLevel } from "../../features/auth/auth.slice";
import { logout, setMockAccessPreview } from "../../features/auth/auth.slice";
import {
	CHARGES_PAGE_SIZE,
	useGetChargesServicePeriodsQuery,
	useGetChargesUnitInvoiceQuery,
} from "../../features/chargesManagement";
import {
	MONTHLY_PAGE_SIZE,
	resolveMonthlyPropertyName,
	useGetMonthlyServicePeriodsQuery,
	useGetMonthlyUnitStatementQuery,
} from "../../features/monthlyStatements";
import {
	REVENUE_PAGE_SIZE,
	useGetRevenueServicePeriodsQuery,
	useGetRevenueUnitStatementQuery,
} from "../../features/revenueManagement";

const ACCESS_PREVIEW_OPTIONS: {
	value: MockAccessPreviewLevel;
	label: string;
}[] = [
	{ value: "READ", label: "Read" },
	{ value: "READ_WRITE", label: "Read & Write" },
	{ value: "APPROVE", label: "Approver" },
];

function getInitials(input?: string): string {
	if (!input) return "AD";
	const cleaned = input.trim();
	if (!cleaned) return "AD";
	return cleaned
		.split(/\s+/)
		.filter(Boolean)
		.map((n) => n[0])
		.join("")
		.toUpperCase()
		.slice(0, 2);
}

export const Header = () => {
	const user = useSelector((state: RootState) => state.auth.user);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const [logoutMutation] = useLogoutMutation();
	const [profileMenuOpen, setProfileMenuOpen] = useState(false);
	const mockAccessPreview = useSelector(
		(state: RootState) => state.auth.mockAccessPreview,
	);
	const showAccessPreviewToggle = isReadOnlyPreviewAllowed();
	const menuRef = useRef<HTMLDivElement | null>(null);

	const displayName = user?.name ?? user?.email ?? "—";
	const revenuePathParts = location.pathname.split("/").filter(Boolean);
	const revenuePropertyId =
		revenuePathParts[0] === "revenue-management" &&
		revenuePathParts[1] === "properties"
			? (revenuePathParts[2] ?? "")
			: "";
	const revenuePeriodId =
		revenuePathParts[3] === "periods" ? (revenuePathParts[4] ?? "") : "";
	const revenueUnitId =
		revenuePathParts[5] === "units" ? (revenuePathParts[6] ?? "") : "";
	const { data: revenuePeriodsData } = useGetRevenueServicePeriodsQuery(
		{
			propertyId: revenuePropertyId,
			page: 1,
			pageSize: REVENUE_PAGE_SIZE,
		},
		{ skip: !revenuePropertyId },
	);
	const { data: revenueUnitDetail } = useGetRevenueUnitStatementQuery(
		{
			propertyId: revenuePropertyId,
			periodId: revenuePeriodId,
			unitId: revenueUnitId,
		},
		{ skip: !revenuePropertyId || !revenuePeriodId || !revenueUnitId },
	);
	const monthlyPathParts = location.pathname.split("/").filter(Boolean);
	const monthlyPropertyId =
		monthlyPathParts[0] === "monthly-statements" &&
		monthlyPathParts[1] === "properties"
			? (monthlyPathParts[2] ?? "")
			: "";
	const monthlyPeriodId =
		monthlyPathParts[3] === "periods" ? (monthlyPathParts[4] ?? "") : "";
	const monthlyUnitId =
		monthlyPathParts[5] === "units" ? (monthlyPathParts[6] ?? "") : "";
	const { data: monthlyPeriodsData } = useGetMonthlyServicePeriodsQuery(
		{
			propertyId: monthlyPropertyId,
			page: 1,
			pageSize: MONTHLY_PAGE_SIZE,
		},
		{ skip: !monthlyPropertyId },
	);
	const { data: monthlyUnitDetail } = useGetMonthlyUnitStatementQuery(
		{
			propertyId: monthlyPropertyId,
			periodId: monthlyPeriodId,
			statementId: monthlyUnitId,
		},
		{ skip: !monthlyPropertyId || !monthlyPeriodId || !monthlyUnitId },
	);
	const chargesPathParts = location.pathname.split("/").filter(Boolean);
	const chargesPropertyId =
		chargesPathParts[0] === "charges-management" &&
		chargesPathParts[1] === "properties"
			? (chargesPathParts[2] ?? "")
			: "";
	const chargesPeriodId =
		chargesPathParts[3] === "periods" ? (chargesPathParts[4] ?? "") : "";
	const chargesUnitId =
		chargesPathParts[5] === "units" ? (chargesPathParts[6] ?? "") : "";
	const { data: chargesPeriodsData } = useGetChargesServicePeriodsQuery(
		{
			propertyId: chargesPropertyId,
			page: 1,
			pageSize: CHARGES_PAGE_SIZE,
			status: "ALL",
		},
		{ skip: !chargesPropertyId },
	);
	const { data: chargesUnitDetail } = useGetChargesUnitInvoiceQuery(
		{
			propertyId: chargesPropertyId,
			periodId: chargesPeriodId,
			unitId: chargesUnitId,
		},
		{ skip: !chargesPropertyId || !chargesPeriodId || !chargesUnitId },
	);

	const moduleTitle = useMemo(() => {
		const path = location.pathname;
		if (path.startsWith("/users")) return "User Management";
		if (path.startsWith("/properties")) return "Property Management";
		if (path.startsWith("/property-marketing")) return "Property Marketing";
		if (path.startsWith("/events")) return "Event Management";
		if (path.startsWith("/restaurant-booking")) return "F&B Management";
		if (path.startsWith("/approvals")) return "Beneficiary Approvals";
		if (path.startsWith("/roles-permissions")) return "Roles & Permissions";
		if (path.startsWith("/sub-admin")) return "Kerzner Sub Admin Management";
		if (path.startsWith("/app-update")) return "App Update";
		if (path.startsWith("/static-content")) return "Static Content";
		if (path.startsWith("/revenue-management")) return "Revenue Management";
		if (path.startsWith("/charges-management")) return "Charges Management";
		if (path.startsWith("/monthly-statements")) return "Monthly Statement";
		if (path.startsWith("/profile")) return "Profile";
		return "Kerzner Residences";
	}, [location.pathname]);

	const moduleSubtitle = useMemo(() => {
		const path = location.pathname;
		if (path.startsWith("/users")) {
			return "Manage homeowners and their family members across all properties";
		}
		if (path.startsWith("/properties")) {
			return "Manage all Kerzner properties across the portfolio";
		}
		if (path.startsWith("/property-marketing")) {
			return "Manage marketing campaigns and promotions across properties";
		}
		if (path.startsWith("/events")) {
			return "Create and manage events for all your residents";
		}
		if (path.startsWith("/approvals")) {
			return "Review beneficiary request";
		}
		if (path.startsWith("/roles-permissions")) {
			return "Manage roles and access levels";
		}
		if (path.startsWith("/sub-admin")) {
			return "Manage sub admin users and their property access";
		}
		if (path.startsWith("/restaurant-booking")) {
			if (path === "/restaurant-booking" || path === "/restaurant-booking/") {
				return "View restaurant details";
			}
			if (
				path === "/restaurant-booking/create" ||
				/\/restaurant-booking\/.+\/edit$/.test(path)
			) {
				return "Manage restaurant listings across properties";
			}
			return "View restaurant details";
		}
		if (path.startsWith("/app-update")) {
			return "Manage app update notifications for iOS and Android.";
		}
		if (path.startsWith("/static-content")) {
			if (path === "/static-content" || path === "/static-content/") {
				return "Manage platform-wide documents shown to users";
			}
			const slug = path.split("/")[2] ?? "";
			const labels: Record<string, string> = {
				"terms-and-conditions": "Terms & Conditions",
				"privacy-policy": "Privacy Policy",
				"my-benefits": "My Benefits",
				"about-kerzner": "About Kerzner",
			};
			return labels[slug] ?? "Edit document";
		}
		if (path.startsWith("/revenue-management")) {
			if (path === "/revenue-management" || path === "/revenue-management/") {
				return "Manage rental revenue and owner payout statements across properties";
			}
			const propertyName =
				revenueUnitDetail?.header.propertyName ||
				revenuePeriodsData?.propertyName;
			const periodLabel =
				revenueUnitDetail?.header.periodLabel ||
				(revenuePeriodId
					? revenuePeriodsData?.items.find(
							(item) => item.id === revenuePeriodId,
						)?.periodLabel
					: undefined);
			if (revenueUnitDetail && propertyName) {
				return `${propertyName} · ${revenueUnitDetail.unitCode} · Statement ${revenueUnitDetail.statementId}`;
			}
			if (propertyName && periodLabel) {
				return `${propertyName} · ${periodLabel}`;
			}
			if (propertyName) {
				return `Service periods · ${propertyName}`;
			}
			return "Manage rental revenue and owner payout statements across properties";
		}
		if (path.startsWith("/charges-management")) {
			if (path === "/charges-management" || path === "/charges-management/") {
				return "Manage HOA charges, service periods and homeowner payments";
			}
			const propertyName =
				chargesUnitDetail?.header.propertyName ||
				chargesPeriodsData?.propertyName;
			const periodLabel =
				chargesUnitDetail?.header.periodLabel ||
				(chargesPeriodId
					? chargesPeriodsData?.items.find(
							(item) => item.id === chargesPeriodId,
						)?.periodLabel
					: undefined);
			if (chargesUnitDetail && propertyName) {
				return `${propertyName} · ${chargesUnitDetail.unitCode} · Invoice ${chargesUnitDetail.invoiceNumber}`;
			}
			if (propertyName && periodLabel) {
				return `${propertyName} · ${periodLabel}`;
			}
			if (propertyName) {
				return `Service periods · ${propertyName}`;
			}
			return "Manage HOA charges, service periods and homeowner payments";
		}
		if (path.startsWith("/monthly-statements")) {
			if (path === "/monthly-statements" || path === "/monthly-statements/") {
				return "Manage consolidated HOA monthly statements across all properties";
			}
			const onUnitPage = Boolean(monthlyUnitId);
			const propertyName = resolveMonthlyPropertyName(
				onUnitPage ? monthlyUnitDetail?.propertyName : undefined,
				monthlyPeriodsData?.propertyName,
			);
			const periodLabel =
				(onUnitPage ? monthlyUnitDetail?.servicePeriodLabel : undefined) ||
				(monthlyPeriodId
					? monthlyPeriodsData?.items.find(
							(item) => item.id === monthlyPeriodId,
						)?.periodLabel
					: undefined);
			if (onUnitPage && monthlyUnitDetail) {
				return `${propertyName} · ${monthlyUnitDetail.unitCode} · Report ${monthlyUnitDetail.reportId}`;
			}
			if (propertyName !== "Untitled property" && periodLabel) {
				return `${propertyName} · ${periodLabel}`;
			}
			if (propertyName !== "Untitled property") {
				return `Statement periods · ${propertyName}`;
			}
			return "Manage consolidated HOA monthly statements across all properties";
		}
		return "";
	}, [
		location.pathname,
		chargesPeriodId,
		chargesPeriodsData,
		chargesUnitDetail,
		monthlyPeriodId,
		monthlyPeriodsData,
		monthlyUnitDetail,
		monthlyUnitId,
		revenuePeriodId,
		revenuePeriodsData,
		revenueUnitDetail,
	]);

	useEffect(() => {
		function onDocClick(e: MouseEvent) {
			if (!menuRef.current) return;
			if (!menuRef.current.contains(e.target as Node)) {
				setProfileMenuOpen(false);
			}
		}
		document.addEventListener("mousedown", onDocClick);
		return () => document.removeEventListener("mousedown", onDocClick);
	}, []);

	const handleLogout = async () => {
		try {
			await logoutMutation().unwrap();
		} catch (_error) {
			// Ignore API failure, still clear local state.
		}
		dispatch(logout());
		navigate("/login", { replace: true });
	};

	return (
		<header className="min-h-[var(--height-header)] border-b border-border bg-bg-card px-6 py-3">
			<div className="flex items-start justify-between gap-4">
				<div className="min-w-0">
					<h1 className="truncate text-xl font-bold leading-7 text-primary">
						{moduleTitle}
					</h1>
					{moduleSubtitle && (
						<p className="mt-1 text-base leading-6 text-primary-muted">
							{moduleSubtitle}
						</p>
					)}
				</div>
				<div className="flex shrink-0 items-center gap-3">
					{showAccessPreviewToggle ? (
						<label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-bg-input px-3 py-2 text-sm text-input-text">
							<span className="whitespace-nowrap">Preview access</span>
							<select
								value={mockAccessPreview}
								onChange={(e) =>
									dispatch(
										setMockAccessPreview(
											e.target.value as MockAccessPreviewLevel,
										),
									)
								}
								aria-label="Preview access level"
								className="h-8 min-w-[140px] rounded-md border border-border bg-bg-card px-2 text-sm text-input-text"
							>
								{ACCESS_PREVIEW_OPTIONS.map((option) => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</select>
						</label>
					) : null}
					<div className="relative" ref={menuRef}>
						<button
							type="button"
							onClick={() => setProfileMenuOpen((v) => !v)}
							className="inline-flex items-center gap-2 rounded-xl border border-border bg-bg-card px-2.5 py-1.5 text-left hover:bg-bg-input"
						>
							<div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-accent text-sm text-white">
								{getInitials(user?.name ?? user?.email)}
							</div>
							<div className="max-w-[160px] truncate text-sm font-medium text-primary">
								{displayName}
							</div>
							<ChevronDown className="h-4 w-4 text-primary-muted" />
						</button>
						{profileMenuOpen && (
							<div className="absolute right-0 z-30 mt-2 w-48 overflow-hidden rounded-xl border border-border bg-bg-card shadow-lg">
								<button
									type="button"
									onClick={() => {
										setProfileMenuOpen(false);
										navigate("/profile");
									}}
									className="flex w-full items-center gap-2 px-3 py-2 text-sm text-input-text hover:bg-bg-input"
								>
									<UserIcon className="h-4 w-4" />
									Profile
								</button>
								<button
									type="button"
									onClick={handleLogout}
									className="flex w-full items-center gap-2 border-t border-border px-3 py-2 text-sm text-status-invite-expired hover:bg-red-50"
								>
									<LogOut className="h-4 w-4" />
									Logout
								</button>
							</div>
						)}
					</div>
				</div>
			</div>
		</header>
	);
};
